// src/pages/RidePaymentPage.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "17px",
      color: "#2d3748",
      fontFamily: "Poppins, sans-serif",
      "::placeholder": { color: "#a0aec0" },
    },
  },
  hidePostalCode: true,
};

const RidePaymentPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);

  const rideId = query.get("rideId");
  const from = query.get("from");
  const to = query.get("to");
  const message = query.get("message") || "";

  // 🔥 added — price sent from RidePreview (stop or seat price)
  const stopPrice = Number(query.get("stopPrice")) || null;
  const finalPrice = Number(query.get("finalPrice")) || null;

  const customerId = localStorage.getItem("customerId");

  const [ride, setRide] = useState(null);
  const [booking, setBooking] = useState(null);
  const [pricing, setPricing] = useState({ base: 0, fee: 4.99, total: 4.99 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* =========================================================
     📡 Fetch ride details
  ========================================================= */
  useEffect(() => {
    if (!rideId) return;

    const fetchRide = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/ride/preview/${rideId}`);
        const rideData = res.data;

        // 🔥 Determine correct base price
        const seatPrice = Number(rideData.pricePerSeat) || 0;
        const basePrice =
          stopPrice || finalPrice || seatPrice;

        const bookingFee = 4.99;
        const total = parseFloat((basePrice + bookingFee).toFixed(2));

        setRide(rideData);
        setPricing({ base: basePrice, fee: bookingFee, total });
      } catch (err) {
        console.error("❌ Ride fetch error:", err);
        setError("Could not load ride details.");
      }
    };

    fetchRide();
  }, [rideId, stopPrice, finalPrice]);

  /* =========================================================
     🔎 Check existing booking
  ========================================================= */
  useEffect(() => {
    if (!rideId || !customerId) return;
    const fetchBooking = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/ridepayment/check-booking`,
          { params: { rideId, customerId } }
        );
        if (res.data) setBooking(res.data);
      } catch {
        setBooking(null);
      }
    };
    fetchBooking();
  }, [rideId, customerId]);

  /* =========================================================
     💳 Handle Payment
  ========================================================= */
  const handlePayment = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    if (!stripe || !elements) {
      setError("Stripe is not ready yet.");
      setLoading(false);
      return;
    }

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Card element missing.");

      // 1️⃣ Create PaymentIntent
      const intentRes = await axios.post(`${API_BASE}/api/ridepayment/create-intent`, {
        rideId,
        customerId,
        basePrice: pricing.base,    // ⭐ main fix
        finalPrice: pricing.base,   // ⭐ stop or seat price
        seatsRequested: 1,

      });

      const { clientSecret, paymentIntentId, total } = intentRes.data;
      const totalPrice = total;

      // 2️⃣ Confirm payment
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: localStorage.getItem("userName") || "Customer",
            },
          },
        });

      if (confirmError) throw new Error(confirmError.message);

      // 3️⃣ Save booking
      if (paymentIntent.status === "succeeded") {
         await axios.post(`${API_BASE}/api/ridepayment/confirm-booking`, {
  rideId,
  customerId,

  // ⭐ NORMAL ROUTE
  from,
  to,

  message,
  paymentIntentId,

  // ⭐ STOP LOGIC
  segmentFrom: from,
  segmentTo: to,
  segmentPrice: pricing.base,   // this is your stop or seat price
  isStop: Boolean(stopPrice),   // TRUE if a stop was selected

  // ⭐ STRIPE TOTAL
  totalPaid: totalPrice,        // includes booking fee
});

        setSuccess(
          `✅ Payment of $${totalPrice.toFixed(2)} Recieved! Ride requested.`
        );

        setTimeout(() => navigate("/your-trips"), 2000);
      } else {
        throw new Error("Unexpected payment status: " + paymentIntent.status);
      }
    } catch (err) {
      console.error("❌ Payment error:", err);
      setError(err.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     UI
  ========================================================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-100 to-white pt-12 px-4 font-[Poppins]">
      {/* HEADER */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-extrabold text-white drop-shadow-lg">
          Confirm Payment
        </h1>
        <p className="text-blue-900 text-sm mt-1">Secure Stripe Checkout</p>
      </div>

      <div className="max-w-lg mx-auto bg-white p-6 rounded-3xl shadow-2xl space-y-6 border border-gray-100">
        {/* RIDE SUMMARY */}
        {ride && (
          <div className="bg-gray-50 p-4 rounded-xl shadow-sm border">
            <div className="font-semibold text-gray-800 text-lg">
              {ride.from} → {ride.to}
            </div>

            <div className="text-gray-600 text-sm mt-1">
              {ride.date} • {ride.time}
            </div>

            {/* PRICE BREAKDOWN */}
            <div className="mt-4 space-y-1 text-gray-700">
              <div className="flex justify-between">
                <span>Base Fare:</span>
                <span>${pricing.base.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span>Booking Fee:</span>
                <span>${pricing.fee.toFixed(2)}</span>
              </div>

              <div className="border-t mt-2 pt-2 flex justify-between font-bold text-lg text-blue-700">
                <span>Total</span>
                <span>${pricing.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* EXISTING PENDING BOOKING */}
        {booking && booking.status === "pending" ? (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl text-yellow-700 font-medium text-center">
            You already have a pending booking for this ride.
          </div>
        ) : (
          <>
            {/* CARD INPUT */}
            <div className="bg-gray-50 p-4 rounded-xl shadow-sm border">
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Card Details
              </label>

              <div className="border p-3 rounded-xl bg-white shadow-sm">
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-center text-sm">{error}</p>
            )}
            {success && (
              <p className="text-green-600 text-center text-sm">{success}</p>
            )}

            {/* PAY BUTTON */}
            <button
              onClick={handlePayment}
              disabled={loading}
              className="w-full py-3 rounded-full bg-green-600 text-white text-lg font-semibold shadow-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {loading
                ? "Processing..."
                : `Pay $${pricing.total.toFixed(2)} & Request Ride`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RidePaymentPage;
